import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "projectType", "rateSource", "rateOption", "projectRateOption", "fixedFeeOption",
    "standardBudgetHint", "fixedFeeBudgetHint", "budgetSection", "clientField",
    "billableAll", "billableTask", "taskSelected", "taskRateOption", "personRateOption",
    "budgetType", "totalHoursOption", "totalCostOption", "taskHoursOption", "taskFeeOption",
    "taskBudgetHint", "taskTotalsOption", "includeExpensesOption", "budgetAlertEnabled", "budgetAlertThreshold",
    "taskRows", "membershipRows", "taskHoursInput", "taskAmountInput", "taskHoursTotal", "taskAmountTotal"
  ]
  static values = { taskRowUrl: String, membershipRowUrl: String }

  connect() {
    this.pendingAssignments = new Set()
    this.update()
  }

  update() {
    const type = this.projectTypeTargets.find((input) => input.checked)?.value
    const timeAndMaterials = type === "time_and_materials"
    const rateSource = this.rateSourceTarget.value
    const budgetType = this.budgetTypeTarget.value
    const taskRateVisible = timeAndMaterials && rateSource === "task"
    const taskHoursVisible = budgetType === "task_hours"
    const taskFeeVisible = budgetType === "task_fees"

    this.toggle(this.rateOptionTarget, timeAndMaterials)
    this.toggle(this.projectRateOptionTarget, timeAndMaterials && rateSource === "project")
    this.toggle(this.fixedFeeOptionTarget, type === "fixed_fee")
    this.toggle(this.standardBudgetHintTarget, type !== "fixed_fee")
    this.toggle(this.fixedFeeBudgetHintTarget, type === "fixed_fee")
    const budgetSectionClasses = ["mt-6", "border-t", "border-orange-200", "pt-6"]
    budgetSectionClasses.forEach((className) => {
      this.budgetSectionTarget.classList.toggle(className, type !== "non_billable")
    })
    this.billableTaskTargets.forEach((element) => { element.disabled = type === "non_billable" })
    this.billableAllTarget.disabled = type === "non_billable"
    this.taskRateOptionTargets.forEach((element) => this.toggle(element, taskRateVisible))
    this.personRateOptionTargets.forEach((element) => this.toggle(element, timeAndMaterials && rateSource === "person"))
    this.toggle(this.totalHoursOptionTarget, budgetType === "total_hours")
    this.toggle(this.totalCostOptionTarget, budgetType === "total_cost")
    this.taskHoursOptionTargets.forEach((element) => this.toggle(element, taskHoursVisible))
    this.taskFeeOptionTargets.forEach((element) => this.toggle(element, taskFeeVisible))
    this.toggle(this.taskBudgetHintTarget, taskHoursVisible || taskFeeVisible)
    this.toggle(this.taskTotalsOptionTarget, taskHoursVisible || taskFeeVisible)
    this.toggle(this.includeExpensesOptionTarget, budgetType === "total_cost")
    this.budgetAlertThresholdTarget.disabled = !this.budgetAlertEnabledTarget.checked
    this.updateBillableAll()
    this.updateTotals()
  }

  toggleAllBillable() {
    const checked = this.billableAllTarget.checked

    this.billableTaskTargets.forEach((input) => {
      if (input.disabled) return

      input.checked = checked
      input.dispatchEvent(new Event("change", { bubbles: true }))
    })
    this.updateBillableAll()
  }

  updateBillableAll() {
    const items = this.billableTaskTargets.filter((input) => !input.disabled)
    const selected = items.filter((input) => input.checked).length

    this.billableAllTarget.checked = items.length > 0 && selected === items.length
    this.billableAllTarget.indeterminate = selected > 0 && selected < items.length
  }

  updateTotals() {
    const selectedTaskIds = new Set(this.taskSelectedTargets.map((input) => input.value))
    const hours = this.sumInputs(this.taskHoursInputTargets, selectedTaskIds)
    const amount = this.sumInputs(this.taskAmountInputTargets, selectedTaskIds)

    this.taskHoursTotalTarget.textContent = this.formatNumber(hours, 0)
    this.taskAmountTotalTarget.textContent = this.formatNumber(amount, 2)
  }

  sumInputs(inputs, selectedTaskIds) {
    return inputs.reduce((sum, input) => selectedTaskIds.has(input.dataset.taskId) ? sum + this.parseNumber(input.value) : sum, 0)
  }

  parseNumber(value) {
    let normalized = value.trim().replace(/[\s'’]/g, "")
    const separator = Math.max(normalized.lastIndexOf("."), normalized.lastIndexOf(","))
    if (separator >= 0) {
      const whole = normalized.slice(0, separator).replace(/[.,]/g, "")
      const fraction = normalized.slice(separator + 1).replace(/[.,]/g, "")
      normalized = `${whole}.${fraction}`
    }
    const number = Number.parseFloat(normalized)
    return Number.isFinite(number) ? number : 0
  }

  formatNumber(value, minimumFractionDigits) {
    return new Intl.NumberFormat(document.documentElement.lang, { minimumFractionDigits, maximumFractionDigits: 2 }).format(value)
  }

  addTask(event) {
    this.addAssignment(event.detail.record, this.taskRowUrlValue, this.taskRowsTarget, "task")
  }

  addMembership(event) {
    this.addAssignment(event.detail.record, this.membershipRowUrlValue, this.membershipRowsTarget, "membership")
  }

  taskCreated(event) {
    this.addAssignment(event.detail, this.taskRowUrlValue, this.taskRowsTarget, "task")
  }

  async addAssignment(record, urlTemplate, container, type) {
    const id = record.id.toString()
    const key = `${type}:${id}`
    if (this.hasAssignment(container, id) || this.pendingAssignments.has(key)) return

    this.pendingAssignments.add(key)
    try {
      const response = await fetch(urlTemplate.replace("RECORD_ID", encodeURIComponent(id)), { headers: { Accept: "text/html" } })
      if (!response.ok) return

      container.insertAdjacentHTML("beforeend", await response.text())
      this.update()
    } finally {
      this.pendingAssignments.delete(key)
    }
  }

  hasAssignment(container, id) {
    return [...container.querySelectorAll("[data-project-assignment-row]")].some((row) => row.dataset.assignmentId === id)
  }

  removeAssignment(event) {
    event.currentTarget.closest("[data-project-assignment-row]")?.remove()
    this.updateBillableAll()
    this.updateTotals()
  }

  clientCreated(event) {
    const autocomplete = this.clientFieldTarget.querySelector("[data-controller~='autocomplete']")
    const controller = this.application.getControllerForElementAndIdentifier(autocomplete, "autocomplete")
    controller?.select({ id: event.detail.id, text: event.detail.name })
  }

  toggle(element, visible) { element.classList.toggle("hidden", !visible) }
}
